import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  // Detect system preference
  const getSystemTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)')?.matches) {
      return 'dark';
    }
    return 'light';
  };

  // Initialize theme on mount
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // 1. Check localStorage first
        const savedTheme = localStorage.getItem('adt-theme');
        
        if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
          setTheme(savedTheme);
          applyTheme(savedTheme);
        } else {
          // 2. Try to get from Supabase user metadata
          const { data: { user } } = await supabase?.auth?.getUser();
          
          if (user?.user_metadata?.theme) {
            const userTheme = user?.user_metadata?.theme;
            setTheme(userTheme);
            applyTheme(userTheme);
            localStorage.setItem('adt-theme', userTheme);
          } else {
            // 3. Fall back to system preference
            const systemTheme = getSystemTheme();
            setTheme(systemTheme);
            applyTheme(systemTheme);
            localStorage.setItem('adt-theme', systemTheme);
          }
        }
      } catch (error) {
        console.error('Error initializing theme:', error);
        // Fall back to system preference on error
        const systemTheme = getSystemTheme();
        setTheme(systemTheme);
        applyTheme(systemTheme);
      } finally {
        setLoading(false);
      }
    };

    initializeTheme();
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('adt-theme');
      if (!savedTheme) {
        const newTheme = e?.matches ? 'dark' : 'light';
        setTheme(newTheme);
        applyTheme(newTheme);
      }
    };

    mediaQuery?.addEventListener('change', handleChange);
    return () => mediaQuery?.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  const applyTheme = (newTheme) => {
    if (newTheme === 'dark') {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
  };

  // Toggle theme function
  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    applyTheme(newTheme);
    
    // Save to localStorage
    localStorage.setItem('adt-theme', newTheme);
    
    // Try to save to Supabase user metadata
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.auth?.updateUser({
          data: { theme: newTheme }
        });
      }
    } catch (error) {
      console.error('Error saving theme to Supabase:', error);
      // Continue even if Supabase save fails - localStorage is primary
    }
  };

  // Set specific theme
  const setThemeMode = async (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;
    
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('adt-theme', newTheme);
    
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.auth?.updateUser({
          data: { theme: newTheme }
        });
      }
    } catch (error) {
      console.error('Error saving theme to Supabase:', error);
    }
  };

  const value = {
    theme,
    toggleTheme,
    setTheme: setThemeMode,
    isDark: theme === 'dark',
    loading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};